import React, { useState, useEffect } from "react";
import { DataTable } from "@/components/ui/data-table";
import { CouponTableSkeleton } from "@/components/ui/coupon-table-skeleton";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  RuleEngineApi,
  RuleConfiguration,
  GetAllRulesRequest,
  GetAllRulesResponse,
  EarnRule,
  ValidityRule,
  RedemptionRule,
} from "@/api/giftcard.api";
import { useToast } from "@/hooks/use-toast";
import { HandleError } from "@/api/axiosClient";
import { getFormattedDateTime } from "@/utils";
import { Badge } from "@/components/ui/badge";
import { Check, X, Plus, Eye } from "lucide-react";
import { CreateRuleForm } from "./ruleengine/CreateRuleForm";
import { ViewRuleModal } from "./ruleengine/ViewRuleModal";

export default function RuleEngine() {
  const [configurations, setConfigurations] = useState<RuleConfiguration[]>([]);
  const [rulesData, setRulesData] = useState<GetAllRulesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<RuleConfiguration | null>(null);
  const { toast } = useToast();

  const fetchConfigurations = async () => {
    setIsLoading(true);
    try {
      const payload: GetAllRulesRequest = {
        id: 2,
      };
      const response = await RuleEngineApi.getAllRules(payload);
      setRulesData(response);
      if (
        response.listConfiguration &&
        Array.isArray(response.listConfiguration)
      ) {
        setConfigurations(response.listConfiguration);
      } else {
        setConfigurations([]);
      }
      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Warning",
          description: response.error_msg,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching rule configurations:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to fetch rule configurations",
        variant: "destructive",
      });
      setConfigurations([]);
      setRulesData(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigurations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (dateString: string): string => {
    if (!dateString || dateString === "0001-01-01T00:00:00") {
      return "-";
    }
    return getFormattedDateTime(dateString) || "-";
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Rule Engine</h1>
          <p className="text-muted-foreground">
            Manage and configure rules for your loyalty program
          </p>
        </div>
        <Button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center gap-2 bg-green-600 text-white hover:bg-green-700"
        >
          <Plus className="h-4 w-4" />
          Add Rule
        </Button>
      </div>

      <div className="space-y-6">
        {isLoading ? (
          <CouponTableSkeleton rowCount={5} />
        ) : (
          <DataTable
            title=""
            data={configurations}
            cellClassName="py-6 text-gray-700"
            columns={[
              {
                header: "ID",
                accessorKey: "ruleId" as keyof RuleConfiguration,
                cell: (row: RuleConfiguration) => String(row.ruleId),
              },
              {
                header: "Title",
                accessorKey: "title" as keyof RuleConfiguration,
              },
              {
                header: "Earn Rule",
                id: "isEarnRule",
                cell: (row: RuleConfiguration) => (
                  <Badge
                    variant={row.isEarnRule ? "default" : "secondary"}
                    className="flex items-center gap-1 w-fit"
                  >
                    {row.isEarnRule ? (
                      <Check className="h-3 w-3" />
                    ) : (
                      <X className="h-3 w-3" />
                    )}
                    {row.isEarnRule ? "Yes" : "No"}
                  </Badge>
                ),
              },
              {
                header: "Validity Rule",
                id: "isValidityRule",
                cell: (row: RuleConfiguration) => (
                  <Badge
                    variant={row.isValidityRule ? "default" : "secondary"}
                    className="flex items-center gap-1 w-fit"
                  >
                    {row.isValidityRule ? (
                      <Check className="h-3 w-3" />
                    ) : (
                      <X className="h-3 w-3" />
                    )}
                    {row.isValidityRule ? "Yes" : "No"}
                  </Badge>
                ),
              },
              {
                header: "Redemption Rule",
                id: "isRedemptionRule",
                cell: (row: RuleConfiguration) => (
                  <Badge
                    variant={row.isRedemptionRule ? "default" : "secondary"}
                    className="flex items-center gap-1 w-fit"
                  >
                    {row.isRedemptionRule ? (
                      <Check className="h-3 w-3" />
                    ) : (
                      <X className="h-3 w-3" />
                    )}
                    {row.isRedemptionRule ? "Yes" : "No"}
                  </Badge>
                ),
              },
              {
                header: "Created Date",
                id: "createddatetime",
                cell: (row: RuleConfiguration) =>
                  formatDate(row.createddatetime),
              },
              {
                header: "Updated Date",
                id: "updatedDatetime",
                cell: (row: RuleConfiguration) =>
                  formatDate(row.updatedDatetime),
              },
              {
                header: "Action",
                id: "action",
                cell: (row: RuleConfiguration) => (
                      <Button 
                        variant="outline" 
                        size="sm" 
                    onClick={() => {
                      setSelectedRule(row);
                      setIsViewModalOpen(true);
                    }}
                    className="flex items-center gap-2"
                      >
                    <Eye className="h-4 w-4" />
                    View
                      </Button>
                ),
              },
            ]}
          />
        )}
              </div>
              
      {/* Create Rule Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Rule</DialogTitle>
          </DialogHeader>
          <CreateRuleForm
            onSuccess={() => {
              setIsCreateModalOpen(false);
              fetchConfigurations();
            }}
            onCancel={() => {
              setIsCreateModalOpen(false);
            }}
        />
        </DialogContent>
      </Dialog>

      {/* View Rule Modal */}
      {selectedRule && rulesData && (
        <ViewRuleModal
          rule={selectedRule}
          rulesData={rulesData}
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedRule(null);
          }}
          onUpdate={() => {
            setIsViewModalOpen(false);
            setSelectedRule(null);
            fetchConfigurations();
          }}
        />
      )}
    </div>
  );
}
